import React, { useEffect, useState, useRef } from "react";
import { collection, addDoc, getDocs, updateDoc, deleteDoc, doc } from "firebase/firestore";
import { db } from "../firebaseConfig";
import "./Projects.css"; // Importing the CSS file
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";

const Projects = () => {
  const [maps, setMaps] = useState([]);
  const [newMap, setNewMap] = useState({
    siteName: "",
    location: "",
    notes: "",
    fileUpload: [], // Add file upload field
  });
  const [propertiesList, setPropertiesList] = useState([]); // List of properties
  const [selectedMap, setSelectedMap] = useState(null); // For map details
  const [isEditing, setIsEditing] = useState(false); // Inline editing state
  const [editedMap, setEditedMap] = useState({});
  const [showAddMapPopup, setShowAddMapPopup] = useState(false); // State to manage popup visibility
  const [showDetailsPopup, setShowDetailsPopup] = useState(false); // State to manage details popup visibility
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false); // State to manage delete confirmation popup
  const [isUploading, setIsUploading] = useState(false); // State to manage file upload status
  const [isDrawing, setIsDrawing] = useState(false);
  const [lastPos, setLastPos] = useState({ x: 0, y: 0 });

  const storage = getStorage();

  const handleFileUpload = async (e) => {
    const files = e.target.files;
    const uploadedFiles = [];
    setIsUploading(true);

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const storageRef = ref(storage, `maps/${newMap.siteName}/${file.name}`);
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);
      uploadedFiles.push(downloadURL);
    }

    setNewMap((prev) => ({ ...prev, fileUpload: uploadedFiles }));
    setIsUploading(false);
  };

  const handleFileUploadForEdit = async (e) => {
    const files = e.target.files;
    const uploadedFiles = [...editedMap.fileUpload];
    setIsUploading(true);

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const storageRef = ref(storage, `maps/${editedMap.siteName}/${file.name}`);
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);
      uploadedFiles.push(downloadURL);
    }

    setEditedMap((prev) => ({ ...prev, fileUpload: uploadedFiles }));
    setIsUploading(false);
  };

  const handleDeleteImage = async (index) => {
    const fileURL = editedMap.fileUpload[index];
    const fileRef = ref(storage, fileURL);

    try {
      await deleteObject(fileRef);
      const updatedFiles = editedMap.fileUpload.filter((_, i) => i !== index);
      setEditedMap((prev) => ({ ...prev, fileUpload: updatedFiles }));
    } catch (error) {
      console.error("Error deleting file:", error);
    }
  };

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const projectsRef = collection(db, "projects"); // ✅ correct collection
        const querySnapshot = await getDocs(projectsRef);
        const projectList = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setMaps(projectList);
      } catch (error) {
        console.error("Error fetching projects:", error);
      }
    };

    const fetchProperties = async () => {
      try {
        const propertiesRef = collection(db, "properties"); // Fetch from 'properties' collection
        const querySnapshot = await getDocs(propertiesRef);
        const propertiesList = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setPropertiesList(propertiesList);
      } catch (error) {
        console.error("Error fetching properties:", error);
      }
    };

    fetchProjects();
    fetchProperties(); // Fetch properties on component mount
  }, []);

  // Sort propertiesList alphabetically by propertyName
  const sortedPropertiesList = propertiesList.sort((a, b) => a.propertyName.localeCompare(b.propertyName));

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewMap((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddMap = async () => {
    // Check for mandatory fields
    const mandatoryFields = ["siteName", "location"];

    for (const field of mandatoryFields) {
      if (!newMap[field]) {
        alert(`Please fill out the ${field} field.`);
        return;
      }
    }

    if (isUploading) {
      alert("Files are still uploading. Please wait.");
      return;
    }

    setIsUploading(true);
    setTimeout(async () => {
      try {
        const projectsRef = collection(db, "projects"); // Save to 'projects' collection
        const docRef = await addDoc(projectsRef, newMap);
        setMaps([...maps, { id: docRef.id, ...newMap }]);
        setNewMap({
          siteName: "",
          location: "",
          notes: "",
          fileUpload: [], // Reset file upload field
        });
        setShowAddMapPopup(false); // Close the popup after saving
      } catch (error) {
        console.error("Error adding project:", error);
      } finally {
        setIsUploading(false);
      }
    }, 2000); // 2 seconds delay
  };

  const handleEditClick = (map) => {
    setIsEditing(true);
    setEditedMap({ ...map });
  };

  const handleInputChangeForEdit = (e) => {
    const { name, value } = e.target;
    setEditedMap((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveChanges = async () => {
    if (isUploading) {
      alert("Files are still uploading. Please wait.");
      return;
    }

    setIsUploading(true);
    setTimeout(async () => {
      try {
        const mapDoc = doc(db, "maps", editedMap.id);
        await updateDoc(mapDoc, editedMap);
        setMaps((prevMaps) =>
          prevMaps.map((map) =>
            map.id === editedMap.id ? { ...editedMap } : map
          )
        );
        setIsEditing(false);
        setShowDetailsPopup(false);
        setSelectedMap(null);
      } catch (error) {
        console.error("Error saving map changes:", error);
      } finally {
        setIsUploading(false);
      }
    }, 2000); // 2 seconds delay
  };

  const handleDeleteMap = async () => {
    try {
      const mapDoc = doc(db, "maps", selectedMap.id);
      await deleteDoc(mapDoc);
      setMaps(maps.filter((map) => map.id !== selectedMap.id));
      setShowDetailsPopup(false);
      setSelectedMap(null);
      setShowDeleteConfirmation(false);
    } catch (error) {
      console.error("Error deleting map:", error);
    }
  };

  const handleDeleteConfirmation = () => {
    setShowDeleteConfirmation(true);
  };

  const confirmDelete = async () => {
    try {
      const mapDoc = doc(db, "maps", selectedMap.id);
      await deleteDoc(mapDoc);
      setMaps(maps.filter((map) => map.id !== selectedMap.id));
      setShowDetailsPopup(false);
      setSelectedMap(null);
      setShowDeleteConfirmation(false);
    } catch (error) {
      console.error("Error deleting map:", error);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirmation(false);
  };

  const handleDetailsClick = (project) => {
    setSelectedMap(project);
    setEditedMap(project);
    if (project.fileUpload && project.fileUpload[0]) {
      setSelectedImage(project.fileUpload[0]); // ✅ show uploaded image
    }
    setShowDetailsPopup(true);
    setIsEditing(false);
  };

  const handleDrawOnImage = (imageUrl) => {
    if (imageUrl) {
      setSelectedImage(imageUrl);
      setShowDetailsPopup(false);
    }
  };

  const closeModal = () => {
    setShowAddMapPopup(false);
    setShowDetailsPopup(false);
    setSelectedMap(null);
    setNewMap({
      siteName: "",
      location: "",
      notes: "",
      fileUpload: [],
    });
  };

  const [selectedImage, setSelectedImage] = useState(null); // State for the uploaded image
  const [annotations, setAnnotations] = useState([]); // State for marked points and their info
  const [currentAnnotation, setCurrentAnnotation] = useState({
    x: 0,
    y: 0,
    address: "",
    location: "",
    color: "",
    notes: "",
  }); // State for the current annotation
  const canvasRef = useRef(null); // Ref for the canvas

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => setSelectedImage(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleCanvasClick = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    setCurrentAnnotation((prev) => ({ ...prev, x, y }));
  };

  const handleAnnotationChange = (e) => {
    const { name, value } = e.target;
    setCurrentAnnotation((prev) => ({ ...prev, [name]: value }));
  };

  const saveAnnotation = () => {
    setAnnotations((prev) => [...prev, currentAnnotation]);
    setCurrentAnnotation({ x: 0, y: 0, address: "", location: "", color: "", notes: "" });
  };

  const saveAnnotatedImage = async () => {
    const canvas = canvasRef.current;
    const annotatedImage = canvas.toDataURL("image/png"); // Export the canvas as a data URL
  
    try {
      let projectId = selectedMap?.id;
      let projectName = selectedMap?.location || "New Project";
      let siteName = selectedMap?.siteName || "Unnamed Site";
  
      // If no project is selected, create a new project
      if (!selectedMap) {
        const newProject = {
          siteName,
          location: projectName,
          notes: "Auto-created project for annotated image",
          fileUpload: [],
          annotatedImages: [],
        };
        const projectsRef = collection(db, "projects");
        const docRef = await addDoc(projectsRef, newProject);
        projectId = docRef.id;
      }
  
      // Update the project with the annotated image
      const projectDoc = doc(db, "projects", projectId);
      await updateDoc(projectDoc, {
        annotatedImages: [
          ...(selectedMap?.annotatedImages || []), // Append to existing annotated images
          {
            image: annotatedImage,
            address: currentAnnotation.address,
            location: currentAnnotation.location,
            color: currentAnnotation.color,
            notes: currentAnnotation.notes,
            timestamp: new Date(),
          },
        ],
      });
  
      // Save to the 'projectImages' collection
      const projectImagesRef = collection(db, "projectImages");
      await addDoc(projectImagesRef, {
        siteName,
        projectName,
        notes: currentAnnotation.notes,
        annotatedImage,
        address: currentAnnotation.address,
        location: currentAnnotation.location,
        color: currentAnnotation.color,
        timestamp: new Date(),
      });
  
      alert("Annotated image saved successfully!");
      setAnnotations([]); // Clear annotations after saving
    } catch (error) {
      console.error("Error saving annotated image:", error);
      alert("Failed to save the annotated image. Please try again.");
    }
  };
  
  const [annotatedImages, setAnnotatedImages] = useState([]);

useEffect(() => {
  const fetchAnnotatedImages = async () => {
    const snapshot = await getDocs(collection(db, "projectImages"));
    const list = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    setAnnotatedImages(list);
  };
  fetchAnnotatedImages();
}, []);

useEffect(() => {
  if (selectedImage && canvasRef.current) {
    const fetchAndDraw = async () => {
      try {
        console.log("Fetching image:", selectedImage); // Debugging log
        const response = await fetch(selectedImage, { mode: "cors" });
        if (!response.ok) {
          throw new Error(`Failed to fetch image: ${response.statusText}`);
        }
        const blob = await response.blob();
        const reader = new FileReader();
        reader.onload = () => {
          const img = new Image();
          img.onload = () => {
            const canvas = canvasRef.current;
            const ctx = canvas.getContext("2d");
            ctx.clearRect(0, 0, canvas.width, canvas.height); // Clear previous drawings
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height); // Draw the image
          };
          img.src = reader.result;
        };
        reader.readAsDataURL(blob);
      } catch (error) {
        console.error("Error fetching or drawing the image:", error);
        alert("Failed to load the image. Please check the URL or CORS configuration.");
      }
    };

    // Check if the URL is a local data URL (e.g., from FileReader)
    if (selectedImage.startsWith("data:")) {
      console.log("Using local data URL for image."); // Debugging log
      const img = new Image();
      img.onload = () => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");
        ctx.clearRect(0, 0, canvas.width, canvas.height); // Clear previous drawings
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height); // Draw the image
      };
      img.src = selectedImage;
    } else {
      fetchAndDraw();
    }
  }
}, [selectedImage]);

useEffect(() => {
  setAnnotations([]);
}, [selectedImage]);

useEffect(() => {
  const loadImageToCanvas = async () => {
    if (!selectedImage || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    try {
      // Load image as blob using fetch
      const response = await fetch(selectedImage, { mode: "cors" });
      const blob = await response.blob();

      // Convert blob to base64 using FileReader
      const reader = new FileReader();
      reader.onloadend = () => {
        const img = new Image();
        img.crossOrigin = "anonymous"; // 👈 CRITICAL: MUST be set before .src
        img.onload = () => {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        };
        img.onerror = () => {
          alert("Image failed to load.");
        };
        img.src = reader.result;
      };
      reader.readAsDataURL(blob);
    } catch (err) {
      console.error("Image load error:", err);
      alert("Failed to load the image. Please check the URL or CORS settings.");
    }
  };

  loadImageToCanvas();
}, [selectedImage]);


  return (
    <div className="maps-page">
      <h1>Property Maps</h1>
      <button className="blue-button" onClick={() => setShowAddMapPopup(true)}>Click Here to Add Project</button>

      {showAddMapPopup && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>Add Project</h2>
            <div className="form-grid">
              {/* Input Fields */}
              <div>
                <label>Site Name <span className="required">*</span></label>
                <select name="siteName" onChange={handleInputChange} value={newMap.siteName} required>
                  <option value="">Select Site Name</option>
                  {sortedPropertiesList.map(property => (
                    <option key={property.id} value={property.propertyName}>{property.propertyName}</option>
                  ))}
                </select>
              </div>
              <div>
                <label>Project Name <span className="required">*</span></label>
                <input name="location" onChange={handleInputChange} value={newMap.location} />
              </div>
              <div>
                <label>Notes</label>
                <textarea name="notes" onChange={handleInputChange} value={newMap.notes} rows="4" />
              </div>
              <div>
                <label>File Upload</label>
                <input type="file" multiple accept=".pdf,.jpeg,.jpg,.hic,.png,.xslx" onChange={handleFileUpload} />
              </div>
            </div>
            <div className="modal-buttons">
              <button className="blue-button" onClick={handleAddMap} disabled={isUploading}>
                {isUploading ? "Uploading..." : "Save"}
              </button>
              <button className="red-button" onClick={closeModal}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {showDetailsPopup && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>Map Details</h2>
            <div className="popup-content">
              {isEditing ? (
                <div className="form-grid">
                  <div>
                    <label>Site Name</label>
                    <input name="siteName" value={editedMap.siteName || ""} onChange={handleInputChangeForEdit} />
                  </div>
                  <div>
                    <label>Location</label>
                    <input name="location" value={editedMap.location || ""} onChange={handleInputChangeForEdit} />
                  </div>
                  <div>
                    <label>Notes</label>
                    <input name="notes" value={editedMap.notes || ""} onChange={handleInputChangeForEdit} />
                  </div>
                  <div>
                    <label>File Upload</label>
                    <input type="file" multiple accept=".pdf,.jpeg,.jpg,.hic,.png,.xslx" onChange={handleFileUploadForEdit} />
                    {editedMap.fileUpload.map((file, index) => (
                      <div key={index} className="file-item">
                        <a href={file} target="_blank" rel="noopener noreferrer">Image {index + 1}</a>
                        <button className="delete-button" onClick={() => handleDeleteImage(index)}>X</button>
                      </div>
                    ))}
                  </div>
                  <div className="modal-buttons">
                    <button className="blue-button" onClick={handleSaveChanges} disabled={isUploading}>
                      {isUploading ? "Uploading..." : "Save"}
                    </button>
                    <button className="red-button" onClick={() => setIsEditing(false)}>Cancel</button>
                  </div>
                </div>
              ) : (
                <>
                  <p><strong>Site Name:</strong> {selectedMap.siteName}</p>
                  <p><strong>Location:</strong> {selectedMap.location}</p>
                  <p><strong>Notes:</strong> {selectedMap.notes}</p>
                  <p><strong>Files:</strong></p>
                  {Array.isArray(selectedMap.fileUpload) && selectedMap.fileUpload.map((file, index) => (
                    <a key={index} href={file} target="_blank" rel="noopener noreferrer">File {index + 1}</a>
                  ))}
                  <div className="modal-buttons">
                    <button className="blue-button" onClick={() => setIsEditing(true)}>Edit</button>
                    <button
                      className="blue-button"
                      onClick={() => handleDrawOnImage(selectedMap.fileUpload[0])}
                    >
                      Draw on Image
                    </button>
                    <button className="red-button" onClick={handleDeleteConfirmation}>Delete</button>
                    <button className="green-button" onClick={closeModal}>Close</button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirmation && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>Are you sure you want to delete?</h2>
            <div className="modal-buttons">
              <button className="red-button" onClick={confirmDelete}>Yes</button>
              <button className="blue-button" onClick={cancelDelete}>No</button>
            </div>
          </div>
        </div>
      )}

      {/* Rename Property Maps List to Project List */}
      <h2>Project List</h2>
      <table className="maps-table">
        <thead>
          <tr>
            <th>Project Name</th>
            <th>Details</th>
          </tr>
        </thead>
        <tbody>
  {maps.length > 0 ? (
    maps.map((project) => (
      <tr key={project.id}>
        <td>{project.location}</td>
        <td><button onClick={() => handleDetailsClick(project)}>Details</button></td>
      </tr>
    ))
  ) : (
    <tr>
      <td colSpan="2">No projects available.</td>
    </tr>
  )}
</tbody>

      </table>

      <h2>Annotate Image</h2>
      <input type="file" accept="image/*" onChange={handleImageUpload} />
      {selectedImage && (
        <div>
          <canvas
            ref={canvasRef}
            width={800}
            height={600}
            style={{ border: "1px solid black", cursor: "crosshair" }}
            onMouseDown={(e) => {
              const rect = canvasRef.current.getBoundingClientRect();
              setIsDrawing(true);
              setLastPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
            }}
            onMouseMove={(e) => {
              if (!isDrawing) return;
              const canvas = canvasRef.current;
              const ctx = canvas.getContext("2d");
              const rect = canvas.getBoundingClientRect();
              const x = e.clientX - rect.left;
              const y = e.clientY - rect.top;
              ctx.strokeStyle = currentAnnotation.color || "black";
              ctx.lineWidth = 2;
              ctx.beginPath();
              ctx.moveTo(lastPos.x, lastPos.y);
              ctx.lineTo(x, y);
              ctx.stroke();
              setLastPos({ x, y });
            }}
            onMouseUp={() => setIsDrawing(false)}
            onMouseLeave={() => setIsDrawing(false)}
          />
          <div>
            <label>Address:</label>
            <input
              name="address"
              value={currentAnnotation.address}
              onChange={handleAnnotationChange}
            />
            <label>Location:</label>
            <input
              name="location"
              value={currentAnnotation.location}
              onChange={handleAnnotationChange}
            />
            <label>Color:</label>
            <input
              name="color"
              type="color"
              value={currentAnnotation.color}
              onChange={handleAnnotationChange}
            />
            <label>Notes:</label>
            <textarea
              name="notes"
              value={currentAnnotation.notes}
              onChange={handleAnnotationChange}
              rows="4"
            />
            <button onClick={saveAnnotation}>Add Annotation</button>
          </div>
          <button onClick={saveAnnotatedImage}>Save Annotated Image</button>
        </div>
      )}

      <h2>Project Images</h2>
      <table className="maps-table">
        <thead>
          <tr>
            <th>Site Name</th>
            <th>Project Name</th>
            <th>Notes</th>
            <th>Annotated Image</th>
          </tr>
        </thead>
        <tbody>
          {annotatedImages.map((item) => (
            <tr key={item.id}>
              <td>{item.siteName}</td>
              <td>{item.projectName}</td>
              <td>{item.notes}</td>
              <td>
                <img src={item.annotatedImage} alt="Annotated" style={{ width: 200 }} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default Projects;